import { createContext, ReactNode, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import type { QuickSearchSelectionMetadata } from "@/types/QuickSearch";
import { COURSE_ACCESS_PERMISSION } from "@/components/lms/lms.utils";
import { RootState } from "@/components/state/store";

interface ManagerLmsAccessContextValue {
  mode: "full" | "marketing" | "denied";
  canAccess: boolean;
  canAccessMarketing: boolean;
  isLoading: boolean;
  selection: QuickSearchSelectionMetadata | null;
  hasCourseEnrolments: boolean;
}

const ManagerLmsAccessContext = createContext<ManagerLmsAccessContextValue>({
  mode: "denied",
  canAccess: false,
  canAccessMarketing: false,
  isLoading: true,
  selection: null,
  hasCourseEnrolments: false,
});

interface Props {
  children: ReactNode;
  subdomain: string;
}

export function ManagerLmsAccessProvider({ children, subdomain }: Props) {
  const permissions = useSelector(
    (state: RootState) => state.login?.permissions ?? [],
  );
  const selectedCompanySubjectId = useSelector(
    (state: RootState) => state.parent?.subjectId,
  );
  const defaultCompanySubjectId = useSelector(
    (state: RootState) => state.login?.subjectId,
  );
  const managerLmsAccess = useSelector(
    (state: RootState) => state.managerLmsAccess,
  );
  const hasCoursePermission = permissions.includes(COURSE_ACCESS_PERMISSION);
  const effectiveCompanySubjectId = String(
    (subdomain === "manage" ? selectedCompanySubjectId : null) ||
      defaultCompanySubjectId ||
      "",
  );
  const accessMatchesCurrentCompany =
    managerLmsAccess.subjectId === effectiveCompanySubjectId;
  const selection = accessMatchesCurrentCompany
    ? managerLmsAccess.selection
    : null;
  const isLmsAccessSubdomain =
    subdomain === "manage" || subdomain === "portal";
  const isLoading =
    isLmsAccessSubdomain &&
    hasCoursePermission &&
    (managerLmsAccess.status === "idle" ||
      managerLmsAccess.status === "loading" ||
      !accessMatchesCurrentCompany);

  const contextValue = useMemo(
    () => {
      let mode: ManagerLmsAccessContextValue["mode"] = "denied";
      const accessIsReady =
        hasCoursePermission &&
        managerLmsAccess.status === "ready" &&
        accessMatchesCurrentCompany &&
        selection !== null;

      if (!isLmsAccessSubdomain) {
        mode = "full";
      } else if (accessIsReady && subdomain === "manage") {
        mode = selection.selectable ? "full" : "marketing";
      } else if (
        accessIsReady &&
        subdomain === "portal" &&
        selection.selectable &&
        managerLmsAccess.hasCourseEnrolments === true
      ) {
        mode = "full";
      }

      return {
        mode,
        canAccess: mode === "full",
        canAccessMarketing: mode === "marketing",
        isLoading,
        selection,
        hasCourseEnrolments: managerLmsAccess.hasCourseEnrolments === true,
      };
    },
    [
      accessMatchesCurrentCompany,
      hasCoursePermission,
      isLmsAccessSubdomain,
      isLoading,
      managerLmsAccess.hasCourseEnrolments,
      managerLmsAccess.status,
      selection,
      subdomain,
    ],
  );

  return (
    <ManagerLmsAccessContext.Provider value={contextValue}>
      {children}
    </ManagerLmsAccessContext.Provider>
  );
}

export const useManagerLmsAccess = () => useContext(ManagerLmsAccessContext);
